import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { Form, Input, Select, Button, message } from 'antd';
import styled from 'styled-components';
import { ButtonWrapper } from 'components';
import { Box, Flex, Text } from 'atoms';
import { getNodeDetails, updateNode } from 'store/nodeSliceV2';
import { searchPincodeV2 } from 'store/clientSliceV2';
import AddressPickerV2 from 'components/common/AddressPickerV2/AddressPickerV2';
import loadGoogleMapsV2 from 'utils/loadGoogleMapsV2';
import { truncateAddressToCity } from 'utils/addressHelpers';

const { Option } = Select;

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
`;

const FormContainer = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 32px 40px;
	margin-bottom: 24px;
`;

const FormRow = styled(Flex)`
	margin-bottom: 24px;
	gap: 16px;

	&:last-of-type {
		margin-bottom: 0;
	}
`;

const FormItem = styled(Box)`
	flex: 1;

	.ant-form-item-label {
		padding-bottom: 8px;

		label {
			font-size: 13px;
			font-weight: 500;
			color: #333;

			&::before {
				display: none !important;
			}
		}

		label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
			display: inline-block;
			margin-left: 4px;
			color: #ff4d4f;
			font-size: 13px;
			line-height: 1;
			content: '*';
		}
	}

	.ant-form-item-label
		> label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
		display: none !important;
	}

	.ant-input,
	.ant-select-selector {
		border-radius: 4px;
		height: 40px;
		font-size: 13px;
	}

	.ant-input:disabled,
	.ant-input[disabled],
	.ant-select-disabled .ant-select-selector {
		background-color: #f5f5f5;
		color: #666;
		cursor: not-allowed;
	}
`;

const SmallFormItem = styled(FormItem)`
	flex: 0.8;
`;

const MediumFormItem = styled(FormItem)`
	flex: 1.2;
`;

const HeaderTitle = styled(Box)`
	.anticon {
		font-size: 20px;
		color: #333;
		cursor: pointer;
		transition: color 0.3s;

		&:hover {
			color: #1890ff;
		}
	}
`;

const EditButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	font-size: 14px;
	font-weight: 500;
	border-radius: 4px;
	display: flex;
	align-items: center;
	gap: 8px;

	.anticon {
		font-size: 16px;
	}
`;

const NodeTypeOptions = [
	{ value: 'Warehouse', label: 'Warehouse' },
	{ value: 'Store', label: 'Store' },
	{ value: 'Other', label: 'Other' },
];

export default function NodeInformationPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();
	const [form] = Form.useForm();
	const [isEditMode, setIsEditMode] = useState(false);
	const [loading, setLoading] = useState(false);
	const [pageLoading, setPageLoading] = useState(true);
	const [projectSelected, setProjectSelected] = useState(false);
	const [nodeData, setNodeData] = useState(null);

	const loadNodeData = useCallback(async () => {
		setPageLoading(true);
		try {
			const response = await dispatch(getNodeDetails({ node_id: id }));

			if (response?.data?.node) {
				const { node } = response.data;
				setNodeData(node);

				// Extract node address line1 from node_address object
				let addressText = '';
				if (typeof node.node_address === 'object' && node.node_address?.line1) {
					addressText = node.node_address.line1;
				} else if (typeof node.node_address === 'string') {
					addressText = node.node_address;
				}

				// Truncate address to city when loading existing data
				const city = node.city || node.node_address?.city || '';
				const truncatedAddress = addressText
					? truncateAddressToCity(addressText, city)
					: '';

				// Set form values
				form.setFieldsValue({
					project_name: node.project_name,
					node_type: node.node_type,
					node_name: node.node_name,
					node_address: truncatedAddress,
					latitude: node.latitude?.toString(),
					longitude: node.longitude?.toString(),
					poc_name: node.poc_name || '',
					poc_number: node.poc_mobile_number || '',
					pincode: node.pincode || node.node_address?.pincode,
					zone: node.zone,
					city,
					state: node.state || node.node_address?.state,
				});
				setProjectSelected(true);
			}
		} catch (error) {
			message.error('Failed to load node information');
			console.error('Load node error:', error);
		} finally {
			setPageLoading(false);
		}
	}, [dispatch, id, form]);

	// Load Google Maps on component mount
	useEffect(() => {
		loadGoogleMapsV2().catch((err) => {
			console.error('Failed to load Google Maps:', err);
			message.error('Failed to load Google Maps. Please refresh the page.');
		});
	}, []);

	// Load node data when component mounts
	useEffect(() => {
		if (id) {
			loadNodeData();
		}
	}, [id, loadNodeData]);

	const handleProjectChange = (value) => {
		if (value) {
			setProjectSelected(true);
		} else {
			setProjectSelected(false);
		}
	};

	const handleEdit = () => {
		setIsEditMode(true);
	};

	const handleCancel = () => {
		setIsEditMode(false);
		loadNodeData(); // Reload original data
	};

	// Handle pincode lookup - Auto-fetch zone, city, state when pincode is set
	const handlePincodeLookup = async (pincode) => {
		if (pincode?.length === 6 && /^\d{6}$/.test(pincode)) {
			try {
				const result = await dispatch(searchPincodeV2({ pincode }));
				if (result) {
					form.setFieldsValue({
						zone: result.zone || '',
						city: result.city || '',
						state: result.state || form.getFieldValue('state') || '',
					});
				}
			} catch (error) {
				console.error('Pincode lookup failed:', error);
			}
		}
	};

	const handleSave = async () => {
		try {
			const values = await form.validateFields();
			setLoading(true);

			// Prepare update data according to API contract
			const updateData = {
				id,
				node_name: values.node_name,
				node_type: values.node_type,
			};

			// Add node_address if changed (as object structure)
			if (values.node_address) {
				updateData.node_address = {
					line1: values.node_address,
					line2: '',
					city: values.city || '',
					state: values.state || '',
					pincode: values.pincode,
					country: 'India',
				};
			}

			// Add coordinates if changed
			if (values.latitude) {
				updateData.latitude = parseFloat(values.latitude);
			}
			if (values.longitude) {
				updateData.longitude = parseFloat(values.longitude);
			}

			// Add pincode if changed
			if (values.pincode !== nodeData?.pincode) {
				updateData.pincode = values.pincode;
			}

			// Add POC details if changed
			if (values.poc_name) {
				updateData.poc_name = values.poc_name;
			}
			if (values.poc_number) {
				updateData.poc_mobile_number = values.poc_number;
			}

			await dispatch(updateNode({ req_data: updateData }));

			message.success('Node updated successfully');
			// Navigate back to nodes list page
			navigate('/nodes');
		} catch (error) {
			const errorMessage =
				error?.response?.data?.error?.message || 'Failed to update node information';
			message.error(errorMessage);
			console.error('Update node error:', error);
		} finally {
			setLoading(false);
		}
	};

	if (pageLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<Text fontSize="16px" color="#666">
					Loading node information...
				</Text>
			</Box>
		);
	}

	return (
		<Box>
			{/* Header Section */}
			<PageContainer>
				<Flex justifyContent="space-between" alignItems="flex-start" mb={4}>
					<HeaderTitle>
						<Flex alignItems="center">
							<ArrowLeftOutlined onClick={() => navigate('/nodes')} />
							<Box ml={3}>
								<Text fontSize="24px" fontWeight="600" color="#333" mb="4px">
									Node Information
								</Text>
								<Text fontSize="14px" color="#666">
									View or Edit Node Details
								</Text>
							</Box>
						</Flex>
					</HeaderTitle>

					{!isEditMode && (
						<EditButton type="primary" icon={<EditOutlined />} onClick={handleEdit}>
							Edit
						</EditButton>
					)}
				</Flex>
			</PageContainer>

			{/* Node Information Form */}
			<FormContainer>
				<Form form={form} layout="vertical">
					<FormRow>
						<FormItem>
							<Form.Item
								label="Project Name"
								name="project_name"
								required
								rules={[{ required: true, message: 'Project name is required' }]}
							>
								<Select
									placeholder="Select project"
									disabled
									onChange={handleProjectChange}
									showSearch
								>
									<Option value="Licious_Munibai_BTM1_LICI">
										Licious_Munibai_BTM1_LICI
									</Option>
									<Option value="Project_2">Project_2</Option>
								</Select>
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Node Type"
								name="node_type"
								required
								rules={[{ required: true, message: 'Node type is required' }]}
							>
								<Select
									placeholder="Select"
									disabled={!isEditMode || !projectSelected}
									showSearch
								>
									{NodeTypeOptions.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<FormItem>
							<Form.Item
								label="Node Name"
								name="node_name"
								required
								rules={[{ required: true, message: 'Node name is required' }]}
							>
								<Input
									placeholder="Enter node name"
									disabled={!isEditMode || !projectSelected}
								/>
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Node Address"
								name="node_address"
								required
								rules={[{ required: true, message: 'Node address is required' }]}
							>
								<AddressPickerV2
									value={form.getFieldValue('node_address') || ''}
									latitude={form.getFieldValue('latitude') || ''}
									longitude={form.getFieldValue('longitude') || ''}
									onChange={(data) => {
										// Truncate address to city (safeguard - AddressPickerV2 already truncates, but ensure consistency)
										const truncatedAddress = truncateAddressToCity(
											data.address,
											data.city || ''
										);
										form.setFieldsValue({
											node_address: truncatedAddress,
											latitude: data.latitude,
											longitude: data.longitude,
											// Optionally update state and pincode if they exist in form
											state: data.state || form.getFieldValue('state'),
											pincode: data.pincode || form.getFieldValue('pincode'),
										});
										// Auto-fetch zone and city if pincode is valid
										if (
											data.pincode &&
											data.pincode.length === 6 &&
											/^\d{6}$/.test(data.pincode)
										) {
											handlePincodeLookup(data.pincode);
										}
									}}
									disabled={!isEditMode || !projectSelected}
									placeholder="Eg : Intelligent Retail,....."
								/>
							</Form.Item>
						</FormItem>
					</FormRow>

					{/* Latitude and Longitude fields - auto-populated by AddressPicker */}
					<FormRow>
						<FormItem>
							<Form.Item
								label="Latitude"
								name="latitude"
								required
								rules={[{ required: true, message: 'Latitude is required' }]}
							>
								<Input placeholder="Auto-filled from address" readOnly />
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Longitude"
								name="longitude"
								required
								rules={[{ required: true, message: 'Longitude is required' }]}
							>
								<Input placeholder="Auto-filled from address" readOnly />
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<MediumFormItem>
							<Form.Item label="PoC Name" name="poc_name">
								<Input
									placeholder="Eg : AB 01 AB 1234"
									disabled={!isEditMode || !projectSelected}
								/>
							</Form.Item>
						</MediumFormItem>
						<MediumFormItem>
							<Form.Item
								label="PoC Number"
								name="poc_number"
								rules={[
									{
										pattern: /^[0-9]{10}$/,
										message: 'Please enter a valid 10-digit mobile number',
									},
								]}
							>
								<Input
									placeholder="Eg : 9876543210"
									maxLength={10}
									disabled={!isEditMode || !projectSelected}
								/>
							</Form.Item>
						</MediumFormItem>
					</FormRow>

					<FormRow>
						<SmallFormItem>
							<Form.Item
								label="Pincode"
								name="pincode"
								required
								rules={[{ required: true, message: 'Pincode is required' }]}
							>
								<Input
									placeholder="Eg : 517408"
									disabled={!isEditMode || !projectSelected}
								/>
							</Form.Item>
						</SmallFormItem>
						<MediumFormItem>
							<Form.Item label="Zone" name="zone">
								<Input disabled />
							</Form.Item>
						</MediumFormItem>
						<MediumFormItem>
							<Form.Item label="City" name="city">
								<Input disabled />
							</Form.Item>
						</MediumFormItem>
						<MediumFormItem>
							<Form.Item label="State" name="state">
								<Input disabled />
							</Form.Item>
						</MediumFormItem>
					</FormRow>

					{isEditMode && (
						<Flex justifyContent="flex-end" mt={4} gap="16px">
							<Button
								onClick={handleCancel}
								style={{
									minWidth: '120px',
									height: '42px',
									fontSize: '14px',
									fontWeight: '500',
									color: '#FF4D4F',
									borderColor: '#FF4D4F',
								}}
							>
								Cancel
							</Button>
							<ButtonWrapper
								onClick={handleSave}
								loading={loading}
								type="primary"
								style={{
									minWidth: '160px',
									height: '42px',
									fontSize: '14px',
									fontWeight: '500',
									color: '#FFFFFF',
									backgroundColor: '#18405D',
								}}
							>
								Save Changes
							</ButtonWrapper>
						</Flex>
					)}
				</Form>
			</FormContainer>
		</Box>
	);
}
